import React, {
    useState,
    useEffect,
    useContext,
    useMemo,
    useLayoutEffect,
    lazy,
    Suspense,
} from 'react';
import BrevoChatWidget from './components/brevo/brevo-widget.jsx';
import { loadBrevoWidget, setBrevoUserRole } from './utils/brevo';
import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { UserContext } from './context/userContext.js';
import { useSubdomain } from './context/subdomainContext.js';
import { ToastContainer } from 'react-toastify';
import { PageLayout, PublicPageLayoutStyled } from './App.styles.js';
import { getSubdomainClientId } from './utilities/client-id.js';
import 'react-toastify/dist/ReactToastify.css';
import { SidebarInset, SidebarProvider } from './components/ui/sidebar';
import { AppSidebar } from './components/sidebar/app-sidebar';
import { SiteHeader } from './components/sidebar/site-header';
import { AnimatePresence, motion } from 'framer-motion';
import { NavigationProgress, RouteLoadingFallback } from './components/navigation-progress.jsx';
import ErrorBoundary from './components/errors/error-boundary.jsx';
import { useGlobalModalScrollLock } from './hooks/use-body-scroll-lock.js';

// Every route below is lazy-loaded so a visit to one page doesn't pull in the
// JS (and heavy deps like pdf/xlsx/recharts/reactflow/tiptap) for every other
// page. This is what keeps the initial bundle small on first load.
const Showings = lazy(() => import('./pages/showings/showings.pages.jsx'));
const AgentPropertyList = lazy(
    () => import('./pages/agent-property-list/agent-property-list.pages.jsx')
);
const Applications = lazy(() => import('./pages/applications/applications.pages.jsx'));
const Insights = lazy(() => import('./pages/insights/insights.pages.jsx'));
const Deals = lazy(() => import('./pages/deals/deals.pages.jsx'));
const Documents = lazy(() => import('./pages/documents/documents.jsx'));
const Messaging = lazy(() => import('./pages/messaging/messaging.pages.jsx'));
const Properties = lazy(() => import('./pages/properties/properties.pages.jsx'));
const PropertyShowingConfiguration = lazy(
    () => import('./pages/properties/property-showing-configuration.pages.jsx')
);
const PropertyApplicationConfiguration = lazy(
    () => import('./pages/properties/property-application-configuration.pages.jsx')
);
const Leases = lazy(() => import('./pages/leases/leases.pages.jsx'));
const Payments = lazy(() => import('./pages/payments/payments.pages.jsx'));
const ShowingUpdate = lazy(() => import('./pages/agent-showing-update/showing-update.pages.jsx'));
const TenantConfirmSlot = lazy(() => import('./pages/tenant-confirm-slot/tenant-confirm-slot.jsx'));
const Login = lazy(() => import('./pages/login/login.pages.jsx'));
const AgentApplicationDashboard = lazy(
    () => import('./pages/agent-application-dashboard/agent-application-dashboard.jsx')
);
const AgentDashboard = lazy(() => import('./pages/agent-dashboard/agent-dashboard.pages.jsx'));
const RenewalStatus = lazy(() => import('./pages/renewal-status/renewal-status.pages.jsx'));
const ReplacementRequest = lazy(
    () => import('./pages/renewal-replacement/renewal-replacement.pages.jsx')
);
const Settings = lazy(() => import('./pages/settings/settings.pages.jsx'));
const CreateClient = lazy(() => import('./pages/create-client/create-client-form.jsx'));
const Renewals = lazy(() => import('./pages/renewals/renewals.pages.jsx'));
const Leads = lazy(() => import('./pages/leads/leads.pages.jsx'));
const TenantAddTimeslot = lazy(() => import('./pages/tenant-add-timeslot/tenant-add-timeslot.jsx'));
const TenantTourFeedbackPage = lazy(() => import('./pages/tenant-tour-feedback'));
const RemoveProperties = lazy(
    () => import('./pages/remove-properties/remove-properties.pages.jsx')
);
const NotFound = lazy(() => import('./pages/not-found/index.jsx'));
const InsuranceUpload = lazy(() => import('./pages/insurance-upload/insurance-upload.jsx'));
const PreQualificationPage = lazy(() => import('./pages/pre-qualification/index.jsx'));
const LeadAssignmentPage = lazy(() => import('./pages/lead-assignment/index.jsx'));
const ProspectDetailForm = lazy(() => import('./pages/prospect-detail-form/index.jsx'));
const SyndicationLeadForm = lazy(() => import('./pages/lead-form/index.jsx'));
const SchedulingPage = lazy(() => import('./pages/scheduling/index.jsx'));
const RecommendationsPage = lazy(() => import('./pages/recommendations/RecommendationsPage'));
const AllPropertiesPage = lazy(() => import('./pages/properties/AllPropertiesPage.jsx'));
const PropertyDetailPage = lazy(() => import('./pages/lead-form/PropertyDetailPage.jsx'));
const QualificationChatPage = lazy(() => import('./pages/qualification-chat'));
const LandlordApplicationReview = lazy(() => import('./pages/landlord-application-review'));
const LandlordApplications = lazy(() => import('./pages/landlord-applications'));
const OnboardingForm = lazy(() => import('./pages/onboarding/onboarding'));
const Register = lazy(() => import('./pages/register'));
const GuestAgentChatInterface = lazy(() => import('./pages/guest-agent-chat'));
const Support = lazy(() => import('./pages/support/index.jsx'));
const Dashboard = lazy(() => import('./pages/dashboard/index.jsx'));
const ForbiddenError = lazy(() =>
    import('./pages/forbidden/index.jsx').then((m) => ({ default: m.ForbiddenError }))
);
const JoinAs = lazy(() => import('./pages/join-as/index.jsx'));
const LeadApplicantForm = lazy(() => import('./pages/lead-applicant-form/index.jsx'));
const ForgotPassword = lazy(() => import('./pages/forgot-password/forgot-password.jsx'));
const ResetPassword = lazy(() => import('./pages/reset-password/reset-password.jsx'));
const TermsTenantComponent = lazy(() => import('./pages/terms-tenants/index.jsx'));
const ScreeningPaymentReturnPage = lazy(() => import('./pages/screening-payment-return/index.jsx'));
const DeprecatedAgentPublicPage = lazy(
    () => import('./pages/depricated-agent-public-page/index.jsx')
);

const PUBLIC_AGENT_PATHS = ['/', '/application-request', '/showing-request', '/tour-request'];

const getPublicBrevoContext = (pathname) => {
    const normalizedPath = pathname || '/';
    const isPublicAgentPage = PUBLIC_AGENT_PATHS.some((path) =>
        path === '/'
            ? normalizedPath === '/'
            : normalizedPath === path || normalizedPath.startsWith(`${path}/`)
    );
    const clientId = getSubdomainClientId();

    if (isPublicAgentPage) {
        const [, section, propertyId] = normalizedPath.split('/');
        let page = 'public_agent_property_list';
        if (section === 'application-request') {
            page = 'public_agent_application_request';
        } else if (section === 'showing-request' || section === 'tour-request') {
            page = 'public_agent_tour_request';
        }

        return {
            role: 'agent',
            context: {
                page,
                source: 'public_client_subdomain',
                client_id: clientId,
                property_id: propertyId || null,
                public_path: normalizedPath,
            },
        };
    }

    return {
        role: 'applicant',
        context: {
            page: 'public_flow',
            source: 'public_client_subdomain',
            client_id: clientId,
            property_id: null,
            public_path: normalizedPath,
        },
    };
};

export const PublicPageLayout = ({ children }) => {
    const location = useLocation();

    // Add noindex meta tag for public subdomain pages
    useLayoutEffect(() => {
        // Create meta tag
        const metaRobots = document.createElement('meta');
        metaRobots.name = 'robots';
        metaRobots.content = 'noindex';
        metaRobots.id = 'meta-robots-noindex';

        // Add to head
        document.head.appendChild(metaRobots);

        // Cleanup on unmount
        return () => {
            const existingMeta = document.getElementById('meta-robots-noindex');
            if (existingMeta) {
                document.head.removeChild(existingMeta);
            }
        };
    }, []);

    // Default Brevo role for public pages is 'applicant'. Agent-facing public
    // pages and tenant/guarantor flows override it with their audience role.
    useLayoutEffect(() => {
        const { role, context } = getPublicBrevoContext(location.pathname);
        setBrevoUserRole(role, context);
    }, [location.pathname]);

    return <PublicPageLayoutStyled>{children}</PublicPageLayoutStyled>;
};

const TenantAddSlotRedirect = () => {
    const location = useLocation();
    return <Navigate to={`/tenant-hub${location.search}`} replace />;
};

// The applicant/guarantor form now lives on the sign dashboard (a separate
// app on its own subdomain). Old /application-form links that are still in
// circulation (bookmarks, previously-sent emails) get hard-redirected there
// instead of rendering the retired in-app form.
const getSignDashboardOrigin = () => {
    const hostname = window.location.hostname;

    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.endsWith('.localhost')) {
        return 'http://localhost:5174';
    }

    const envMatch = hostname.match(/\.(staging2?|demo)\.leasehub\.app$/);
    return envMatch ? `https://sign.${envMatch[1]}.leasehub.app` : 'https://sign.leasehub.app';
};

const LegacyApplicationFormRedirect = () => {
    const { applicationId, pageType, applicantId } = useParams();
    const location = useLocation();

    useEffect(() => {
        const target = `${getSignDashboardOrigin()}/apply/${applicationId}/${pageType}/${applicantId}${location.search}`;
        window.location.replace(target);
    }, [applicationId, pageType, applicantId, location.search]);

    return null;
};

// Old /lead/:leadId/application/:applicationId/applicant/:applicantId links
// (email/SMS sent before the lead-linked applicant form moved to the sign
// dashboard) carry every id the sign dashboard route needs, so they can be
// hard-redirected the same way as LegacyApplicationFormRedirect. leadId has
// no equivalent on the sign dashboard route and is dropped.
const LegacyLeadApplicationFormRedirect = () => {
    const { applicationId, applicantId } = useParams();
    const location = useLocation();

    useEffect(() => {
        const target = `${getSignDashboardOrigin()}/apply/${applicationId}/applicant/${applicantId}${location.search}`;
        window.location.replace(target);
    }, [applicationId, applicantId, location.search]);

    return null;
};

const TenantShowingUpdateRedirect = ({ forceReschedule = false }) => {
    const location = useLocation();
    const { showingId } = useParams();
    const params = new URLSearchParams(location.search);
    if (forceReschedule && !params.has('reschedule')) {
        params.set('reschedule', 'true');
    }
    const query = params.toString();
    return (
        <Navigate to={`/tenant-showing-update/${showingId}${query ? `?${query}` : ''}`} replace />
    );
};

const canManageLeadAssignment = (user) => user?.userType === 'admin' || user?.isOwner;

const getDefaultLayoutAnimationKey = (pathname) => {
    const [, section] = pathname.split('/');
    const queryAwareDetailSections = new Set([
        'leads',
        'renewals',
        'showings',
        'properties',
        'deals',
        'applications',
    ]);

    if (queryAwareDetailSections.has(section)) {
        return section;
    }

    return pathname;
};

const DefaultLayout = ({ children }) => {
    const location = useLocation();
    const animationKey = getDefaultLayoutAnimationKey(location.pathname);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;

        let timeoutId;
        let idleId;
        const preloadBrevo = () => {
            void loadBrevoWidget({ hideLauncher: true });
        };

        if ('requestIdleCallback' in window) {
            idleId = window.requestIdleCallback(preloadBrevo, { timeout: 3000 });
        } else {
            timeoutId = window.setTimeout(preloadBrevo, 1500);
        }

        return () => {
            if (idleId && 'cancelIdleCallback' in window) {
                window.cancelIdleCallback(idleId);
            }
            if (timeoutId) {
                window.clearTimeout(timeoutId);
            }
        };
    }, []);

    return (
        <main className="tw-h-full tw-w-full">
            <SidebarProvider>
                <AppSidebar className="tailwind-scope" variant="inset" />
                <SidebarInset>
                    <SiteHeader />
                    <ErrorBoundary>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={animationKey + (location.state?.ts || '')}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 100 }}
                                transition={{ duration: 0.3, ease: 'easeOut' }}
                            >
                                {/* Keeps the sidebar and header on screen while a page's code loads. */}
                                <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
                            </motion.div>
                        </AnimatePresence>
                    </ErrorBoundary>
                </SidebarInset>
            </SidebarProvider>
        </main>
    );
};

const App = () => {
    useGlobalModalScrollLock();
    const { isPublicSubdomain } = useSubdomain();
    const [headerHeight, setHeaderHeight] = useState(0);
    const { user, verifyToken, accessToken } = useContext(UserContext);
    // const { authSocket, isSocketReady } = useContext(SocketContext);
    const [isLoading, setIsLoading] = useState(true);
    // const [hasAuthenticated, setHasAuthenticated] = useState(false);

    const location = useLocation();

    const openPaths = [
        '/',
        '/join-as',
        '/register',
        '/property-list',
        '/showing-update',
        '/showing-request',
        '/tour-request',
        '/tenant-showing-update',
        '/tenant-showing-reschedule',
        '/tenant-confirm-slot',
        '/application-request',
        '/application-form',
        '/application-status',
        '/agent-dashboard',
        '/renewal-status',
        '/replacement-request',
        '/tenant-add-slot',
        '/tenant-hub',
        '/tenant-tour-feedback',
        '/guest-agent-chat',
        '/insurance-upload',
        '/scheduling-form',
        '/qualify',
        '/screening-payment-return',
        '/forgot-password',
        '/reset-password',
        '/landlord/application-review',
        '/404',
    ];

    useEffect(() => {
        const checkUserAuthentication = async () => {
            // Check if the current path is an open path

            const isOpenPath = openPaths.some((openPath) =>
                openPath === '/'
                    ? isPublicSubdomain && location.pathname.startsWith(openPath)
                    : location.pathname.startsWith(openPath)
            );

            if (isOpenPath) {
                setIsLoading(false);
            } else {
                verifyToken();
                setIsLoading(false);
            }
            // Note: Adjust setIsLoading(false) according to your state management needs
        };

        checkUserAuthentication();
    }, []);

    // // Effect to handle socket authentication when socket is ready and accessToken is available
    // useEffect(() => {
    //     const isOpenPath = openPaths.some(openPath => location.pathname.startsWith(openPath));
    //     // console.log('Authentication Check:', { isSocketReady, accessToken, isOpenPath });

    //     if (isSocketReady && accessToken && !isOpenPath && !hasAuthenticated) {
    //         // console.log('Authenticating socket...');
    //         authSocket(null, accessToken);
    //         setHasAuthenticated(true); // Prevent re-authentication
    //     }
    // }, [isSocketReady, accessToken, hasAuthenticated, authSocket, location.pathname]);

    // Check the header height and adjust div accordingly
    useEffect(() => {
        const calculateHeaderHeight = () => {
            const headerElement = document.getElementById('header');
            if (headerElement) {
                setHeaderHeight(headerElement.offsetHeight);
            }
        };

        // Calculate initially and on every window resize
        calculateHeaderHeight();
        window.addEventListener('resize', calculateHeaderHeight);

        // Cleanup listener
        return () => window.removeEventListener('resize', calculateHeaderHeight);
    }, []);

    // const DefaultLayout = ({ children }) => (
    //     <>
    //         <SidePanel />
    //         <MainContent>
    //             <Header {...user} />
    //             {children}
    //         </MainContent>
    //     </>
    // );

    if (isLoading) {
        return <></>;
    } else if (isPublicSubdomain) {
        return (
            <PublicPageLayout>
                <div id="modal-root" />
                <ToastContainer
                    position="bottom-right"
                    autoClose={5000}
                    limit={3}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="dark"
                    stacked
                />
                <NavigationProgress />
                <Suspense fallback={<RouteLoadingFallback />}>
                    <Routes>
                        <Route
                            path="/"
                            element={<AgentPropertyList headerHeight={headerHeight} />}
                        />
                        <Route
                            path="/showing-request/:propertyId"
                            element={<DeprecatedAgentPublicPage />}
                        />
                        <Route
                            path="/tour-request/:propertyId"
                            element={<DeprecatedAgentPublicPage />}
                        />
                        {/* REMOVE LATER Allows updating a showing (e.g., confirm/cancel) given a showing ID and update type. (management-reschedule, reschedule, delay, cancel, feedback)  */}
                        <Route
                            path="/showing-update/:showingId/:updateType"
                            element={<ShowingUpdate />}
                        />
                        <Route
                            path="/terms"
                            element={
                                <>
                                    <TermsTenantComponent />
                                </>
                            }
                        />
                        {/* Lets a tenant confirm a time slot for a showing. */}
                        <Route
                            path="/tenant-showing-update/:showingId"
                            element={<TenantConfirmSlot />}
                        />
                        {/* Redirects a tenant to reschedule a showing (forces reschedule mode). */}
                        <Route
                            path="/tenant-showing-reschedule/:showingId"
                            element={<TenantShowingUpdateRedirect forceReschedule />}
                        />
                        {/* Redirects a tenant to confirm a slot (without forcing reschedule). */}
                        <Route
                            path="/tenant-confirm-slot/:showingId"
                            element={<TenantShowingUpdateRedirect />}
                        />
                        <Route path="/tenant-hub" element={<TenantAddTimeslot />} />
                        {/* Redirect wrapper for adding a time slot. */}
                        <Route path="/tenant-add-slot" element={<TenantAddSlotRedirect />} />
                        {/* Page for tenants to submit feedback after a tour. */}
                        <Route path="/tenant-tour-feedback" element={<TenantTourFeedbackPage />} />
                        <Route
                            path="/application-request/:propertyId"
                            element={<DeprecatedAgentPublicPage />}
                        />
                        <Route
                            path="/application-form/:applicationId/:pageType/:applicantId"
                            element={<LegacyApplicationFormRedirect />}
                        />
                        <Route path="/guest-agent-chat" element={<GuestAgentChatInterface />} />
                        <Route
                            path="/agent-dashboard/:internalAgentId/:type?"
                            element={<AgentDashboard />}
                        />
                        <Route
                            path="/application-status/:applicationId"
                            element={<AgentApplicationDashboard />}
                        />
                        {/* Shows a tenant their lease renewal status. */}
                        <Route
                            path="/renewal-status/:renewalId/tenant/:tenantId"
                            element={<RenewalStatus />}
                        />
                        {/*  Lets a tenant submit a roommate/tenant replacement request renewals. */}
                        <Route
                            path="/replacement-request/:renewalId/tenant/:tenantId"
                            element={<ReplacementRequest />}
                        />
                        <Route path="/insurance-upload/:dealId" element={<InsuranceUpload />} />
                        <Route path="/form/:leadId" element={<SyndicationLeadForm />} />
                        <Route path="/qualify/:leadId" element={<QualificationChatPage />} />
                        <Route
                            path="/lead/:leadId/application/new"
                            element={<LeadApplicantForm />}
                        />
                        <Route
                            path="/lead/:leadId/application/:applicationId"
                            element={<LeadApplicantForm />}
                        />
                        <Route
                            path="/lead/:leadId/application/:applicationId/applicant/:applicantId"
                            element={<LegacyLeadApplicationFormRedirect />}
                        />
                        <Route
                            path="/screening-payment-return"
                            element={<ScreeningPaymentReturnPage />}
                        />
                        <Route path="/property/:propertyId" element={<PropertyDetailPage />} />
                        <Route
                            path="/landlord/application-review/:shareId"
                            element={<LandlordApplicationReview />}
                        />
                        <Route path="/recommendations" element={<RecommendationsPage />} />
                        <Route path="/all-properties" element={<AllPropertiesPage />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </Suspense>
                <BrevoChatWidget />
            </PublicPageLayout>
        );
    } else {
        return (
            <PageLayout>
                <div id="modal-root" />
                <ToastContainer
                    position="bottom-right"
                    autoClose={5000}
                    limit={3}
                    hideProgressBar={false}
                    newestOnTop={false}
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    theme="dark"
                    stacked
                />
                <NavigationProgress />
                <Suspense fallback={<RouteLoadingFallback />}>
                    <Routes>
                        <Route
                            path="/dashboard"
                            element={
                                <DefaultLayout>
                                    <Dashboard />
                                </DefaultLayout>
                            }
                        />
                        <Route path="/login" element={<Login />} />
                        <Route path="/qualify/:leadId" element={<QualificationChatPage />} />
                        <Route path="/join-as" element={<JoinAs />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/onboarding" element={<OnboardingForm />} />
                        <Route
                            path="/forgot-password"
                            element={
                                <div className="tailwind-scope tw-h-full tw-w-full">
                                    <ForgotPassword />
                                </div>
                            }
                        />
                        <Route
                            path="/reset-password"
                            element={
                                <div className="tailwind-scope tw-h-full tw-w-full">
                                    <ResetPassword />
                                </div>
                            }
                        />
                        <Route
                            path="/properties/:propertyId/showing-configuration"
                            element={
                                <DefaultLayout>
                                    <PropertyShowingConfiguration />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/properties/:propertyId/application-configuration"
                            element={
                                <DefaultLayout>
                                    <PropertyApplicationConfiguration />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/properties/:propertyId?"
                            element={
                                <DefaultLayout>
                                    <Properties />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/leases/:leaseId?"
                            element={
                                <DefaultLayout>
                                    <Leases />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/landlord/applications/:applicationId?"
                            element={
                                <DefaultLayout>
                                    <LandlordApplications />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/landlord/application-review/:shareId"
                            element={
                                <PublicPageLayout>
                                    <LandlordApplicationReview />
                                </PublicPageLayout>
                            }
                        />
                        <Route
                            path="/showings/:showingId?"
                            element={
                                <DefaultLayout>
                                    <Showings />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/applications/:applicationId?"
                            element={
                                <DefaultLayout>
                                    <Applications />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/insights/:type?/:id?"
                            element={
                                <DefaultLayout>
                                    <Insights />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/messaging"
                            element={
                                <DefaultLayout>
                                    <Messaging />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/deals/:dealId?"
                            element={
                                <DefaultLayout>
                                    <Deals />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/documents/:dealId?"
                            element={
                                <DefaultLayout>
                                    <Documents />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/renewals/:renewalId?"
                            element={
                                <DefaultLayout>
                                    <Renewals />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/payments"
                            element={
                                <DefaultLayout>
                                    <Payments />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <DefaultLayout>
                                    <Settings />
                                </DefaultLayout>
                            }
                        />
                        <Route path="/create-client" element={<CreateClient />} />
                        <Route
                            path="/properties/remove"
                            element={
                                <DefaultLayout>
                                    <RemoveProperties />
                                </DefaultLayout>
                            }
                        ></Route>
                        <Route
                            path="/support"
                            element={
                                <DefaultLayout>
                                    <Support />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/screening-payment-return"
                            element={<ScreeningPaymentReturnPage />}
                        />
                        <Route
                            path="/leads"
                            element={
                                <DefaultLayout>
                                    <Leads />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/scheduling"
                            element={
                                <DefaultLayout>
                                    <SchedulingPage />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/leads/:leadId"
                            element={
                                <DefaultLayout>
                                    <Leads />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/leads/pre-qualification"
                            element={
                                <DefaultLayout>
                                    <PreQualificationPage />
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/leads/assignment"
                            element={
                                <DefaultLayout>
                                    {canManageLeadAssignment(user) ? (
                                        <LeadAssignmentPage />
                                    ) : (
                                        <ForbiddenError />
                                    )}
                                </DefaultLayout>
                            }
                        />
                        <Route
                            path="/prospect-detail-form"
                            element={
                                <DefaultLayout>
                                    <ProspectDetailForm />
                                </DefaultLayout>
                            }
                        />
                        <Route path="/recommendations" element={<RecommendationsPage />} />
                        {/* 403 forbidden page */}
                        <Route
                            path="/403"
                            element={
                                <ForbiddenError
                                    ctaButton={
                                        user?.userType === 'landlord'
                                            ? '/landlord/applications'
                                            : user?.userType === 'accountant'
                                              ? '/payments'
                                              : '/dashboard'
                                    }
                                />
                            }
                        />

                        {/* 404 page */}
                        <Route
                            path="*"
                            element={
                                <NotFound
                                    ctaButton={
                                        user?.userType === 'landlord'
                                            ? '/landlord/applications'
                                            : user?.userType === 'accountant'
                                              ? '/payments'
                                              : '/dashboard'
                                    }
                                />
                            }
                        />
                    </Routes>
                </Suspense>
            </PageLayout>
        );
    }
};

export default App;
